"use client";
import type { RowData } from "@tanstack/react-table";

import { ExternalLink, Link2, SquareArrowRight } from "@/components/icons";
import { useRouter } from "next/navigation";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCopyToClipboard } from "@/hooks/use-copy-to-clipboard";
import { withCurrentReturnContext } from "@/lib/navigation/return-context";
import { openRecordInNewTab } from "./use-row-open";
import { visibleRowActions } from "./use-row-menu";
import type { RowMenuController } from "./use-row-menu";
import type { TableAction } from "../types";
import { isAdminHref, useOpenableHref } from "@/hooks/use-openable-href";

export interface RecordRowMenuTarget {
  rowId: string;
  x: number;
  y: number;
  opener: HTMLElement | null;
}

interface RecordRowMenuProps<TData extends RowData>
  extends Omit<RowMenuController<TData>, "onRowMenu"> {
  actions: TableAction<TData>[] | undefined;
}

export function RecordRowMenu<TData extends RowData>({
  target,
  row,
  href,
  actions,
  close,
}: RecordRowMenuProps<TData>) {
  if (!target || row === undefined) return null;
  const visible = visibleRowActions(actions, row);
  if (href === undefined && visible.length === 0) return null;
  return (
    <DropdownMenu
      open
      onOpenChange={(open) => {
        if (open) return;
        const { opener } = target;
        close();
        if (opener) window.requestAnimationFrame(() => opener.focus());
      }}
    >
      <DropdownMenuTrigger asChild>
        <span
          aria-hidden
          className="pointer-events-none fixed h-0 w-0"
          style={{ left: target.x, top: target.y }}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="z-50 w-[min(220px,calc(100vw-2rem))]"
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        {href ? <RecordLinkItems href={href} /> : null}
        {href && visible.length > 0 ? <DropdownMenuSeparator /> : null}
        {visible.map((action, index) => (
          <RowActionItem
            key={action.label}
            action={action}
            row={row}
            separated={action.separator === true && index > 0}
          />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function RecordLinkItems({ href }: { href: string }) {
  if (isAdminHref(href)) return <GatedRecordLinkItems href={href} />;
  return <OpenRecordLinkItems href={href} />;
}

function GatedRecordLinkItems({ href }: { href: string }) {
  const openable = useOpenableHref(href);
  if (openable === undefined) return null;
  return <OpenRecordLinkItems href={href} />;
}

function OpenRecordLinkItems({ href }: { href: string }) {
  const router = useRouter();
  const { copy } = useCopyToClipboard();
  const copyLink = () =>
    void copy(
      new URL(withCurrentReturnContext(href), window.location.origin).toString(),
      "Link copied",
    );
  return (
    <>
      <DropdownMenuItem
        onClick={() => router.push(withCurrentReturnContext(href))}
      >
        <SquareArrowRight className="mr-2 h-4 w-4" />
        Open
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => openRecordInNewTab(href)}>
        <ExternalLink className="mr-2 h-4 w-4" />
        Open in new tab
      </DropdownMenuItem>
      <DropdownMenuItem onClick={copyLink}>
        <Link2 className="mr-2 h-4 w-4" />
        Copy link
      </DropdownMenuItem>
    </>
  );
}

function RowActionItem<TData extends RowData>({
  action,
  row,
  separated,
}: {
  action: TableAction<TData>;
  row: TData;
  separated: boolean;
}) {
  const router = useRouter();
  return (
    <>
      {separated ? <DropdownMenuSeparator /> : null}
      <DropdownMenuItem
        onClick={() =>
          action.href
            ? router.push(withCurrentReturnContext(action.href(row)))
            : action.onClick(row)
        }
        variant={action.destructive ? "destructive" : "default"}
      >
        {action.icon ? <action.icon className="mr-2 h-4 w-4" /> : null}
        {action.label}
      </DropdownMenuItem>
    </>
  );
}
